// @flow

import * as React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import 'styles/App.scss'

import Masthead from '@ta-interaktiv/react-masthead'
import { ImageCover } from '../cover'
import { TimestampFormatter } from '@ta-interaktiv/react-publication-info'
import sheetrock from 'sheetrock'
import { zip } from 'd3-array'
import { HotelCategory } from 'components/hotelCategory'
import { CategoryMenu } from 'components/categoryMenu'
import { Sidebar } from 'components/slidingpane'
import { RatingExplanationButton } from 'components/ratingExplanationButton'
import { withRail } from 'components/rail'
import { ArticleList } from 'components/articleList'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { json, request } from 'd3-request'
import Tracking from '@ta-interaktiv/react-polymorphic-tracking'
import ShareButtons from '@ta-interaktiv/react-polymorphic-share-buttons'
import { FeedbackMessage } from '@ta-interaktiv/react-feedback-message'
import ArticleTeasers from '@ta-interaktiv/react-article-teasers'
import { PureCopyrightNotice } from '@ta-interaktiv/react-copyright-notice'

const posterImage = require('images/front3.jpg')
const RailedButton = withRail(RatingExplanationButton)

type SheetrockResponse = {
  attributes: {
    last: number,
    rowNumberOffset: 0,
    labels: Array<string>
  },
  html: string,
  options: any,
  raw: {
    version: string,
    reqId: string,
    status: string,
    sig: string,
    table: {
      cols: Array<{
        id: string,
        label: string,
        type: string,
        pattern?: string
      }>,
      rows: Array<{ v: string | number | null, f?: string } | null>
    }
  },
  rows: Array<{ cellsArray: Array<string>, labels: Array<string>, num: number }>
}

type Props = {
  articleId: number
}

type State = {
  request: 'loading' | 'error' | 'ready',
  hotelsWith5Stars: Array<Map>,
  hotelsWith4Stars: Array<Map>,
  hotelsWith3Stars: Array<Map>,
  publishedTimestamp: number,
  communities: []
}

export default class AppComponent extends React.Component<Props, State> {
  static defaultProps = {
    articleId: 26419887
  }

  state = {
    request: 'loading',
    hotelsWith5Stars: [],
    hotelsWith4Stars: [],
    hotelsWith3Stars: [],
    publishedTimestamp: 0,
    communities: []
  }

  componentDidMount () {
    sheetrock({
      url:
        'https://docs.google.com/spreadsheets/d/1Vt53qDDyCedczmBCViGd4EK7Tyvp4GLw0scDtbixhGk/edit?usp=sharing#gid=0',
      query: 'select A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P where A != ""',
      callback: this.prepareData.bind(this)
    })

    const domain = parseHostname()
    json(
      `https://m.${domain.publicationDomain}/api/articles/${this.props
        .articleId}`,
      (error, data) => {
        if (error) {
          return
        }

        this.setState({
          publishedTimestamp: data.article.timestamp_updated_at
        })
      }
    )
    json(
      `https://m.${domain.publicationDomain}/api/articles/${this.props
        .articleId}/external_services`,
      (error, data) => {
        if (error) {
          return
        }

        this.setState({
          communities: data.communities
        })
      }
    )
  }

  /**
   * Prepare the hotel data and set the state once its ready
   * @param error
   * @param options
   * @param response
   */
  prepareData (error, options, response: SheetrockResponse) {
    // Error handling
    if (error) {
      console.group('Main Component 🗂 Sheetrock Loader')
      console.log(error)
      console.groupEnd()
      this.setState({
        request: 'error'
      })
      return
    }

    // Data handling
    console.log(response.rows)

    const mapRows = response.rows.map((row, index) => {
      return new Map(zip(row.labels, row.cellsArray))
    })

    console.assert(mapRows[1].get('Sterne') === '5')

    this.setState({
      hotelsWith5Stars: mapRows.filter(hotel => hotel.get('Sterne') === '5'),
      hotelsWith4Stars: mapRows.filter(hotel => hotel.get('Sterne') === '4'),
      hotelsWith3Stars: mapRows.filter(hotel => hotel.get('Sterne') === '3'),
      request: 'ready'
    })
  }

  defaultClickHandler (community, event): void {
    const shareTypeId = community['share_type_id']
    request(
      `//m.${parseHostname().publicationDomain}/api/communities/${this.props
        .articleId}/counts/${shareTypeId}`
    ).post()
  }

  render () {
    return (
      <div className='index'>
        <Masthead
          mediaName='sonntagszeitung'
          fullMediaName='Sonntags-Zeitung'
          homepage='https://www.sonntagszeitung.ch'
          hashtags={['switzerland', 'hotels', 'holidays']}
          communities={this.state.communities}
          // eslint-disable-next-line react/jsx-no-bind
          clickHandler={this.defaultClickHandler.bind(this)}
        />
        <ImageCover
          poster={posterImage}
          title='Die besten Winterhotels der Schweiz'
          subtitle='Das grosse Rating 2017: Alle Aufsteiger, Absteiger und Neueinsteiger.'
          coverHeight='100vh'
          position='top'
          padding='100px'
          showTextShadow={false}
          showTopGradient={false}
        />

        <div className='ui very fitted vertical segment'>
          <div className='ui container'>
            <p className='ta secondary caption'>
              Erstmals die Topadresse für Winterferien in der Schweiz: Das Kulm
              Hotel St. Moritz .
            </p>
          </div>
        </div>

        <main className='ui vertical segment'>
          <div className='ui text container'>
            <p>
              <cite>Von Karl Wild</cite>
            </p>

            <p>
              Das Kulm Hotel St. Moritz ist erstmals die Topadresse für
              Winterferien in der Schweiz. The Chedi Andermatt, die Nummer eins
              der vergangenen drei Jahre, hat keineswegs nachgelassen. Ganz im
              Gegenteil. Der Sommer war gar der beste in der jungen Geschichte
              des begeisternden Alpen-Hideaways. Aber das Kulm mit seiner
              konkurrenzlosen Angebotsvielfalt von der Natureisbahn über den
              weltberühmten Dracula Club bis zum grandiosen neuen Kulm Country
              Club ist mittlerweile mehr als ein Hotel. Es ist ein kleines
              Alpenreich. Souverän geführt von Heinz und Jenny Hunkeler, den
              Schweizer Hoteliers des Jahres 2016.
            </p>

            <p>
              Aufsteiger des Jahres sind bei den Fünfsternhotels das Guarda Golf
              in Crans-Montana (plus sechs Ränge) sowie das Cervo in Zermatt und
              das St. Moritzer Alpenmonument Suvretta House (je plus vier). Das
              Suvretta House hatte bereits im Vorjahr vier Plätze gewonnen und
              ist somit der eigentliche Überflieger. Dominiert wird die
              Luxusklasse weiterhin von Gstaad und St. Moritz, die je drei
              Häuser in die Top Ten brachten. Erstmals überhaupt gelang dem
              Lenkerhof der Sprung unter die besten zehn.
            </p>

            <RailedButton side='right' className='center aligned' />

            <p>
              Bestes Viersternhotel bleibt für ein weiteres Jahr das Pirmin
              Zurbriggen in Saas-Almagell. Bei den Tophäusern mit drei Sternen
              verteidigte das Spitzhorn in Saanen bei Gstaad Platz eins ebenso
              souverän. Die beiden Alpenperlen schreiben ihre wunderschöne
              Erfolgsstory auf eindrückliche Weise weiter.
            </p>

            <p>
              Zwölf Neulinge schafften den Sprung erstmals (oder wieder) ins
              Rating, das sind so viele wie nie zuvor. Interessantester Newcomer
              ist wohl das 7132 Hotel im Bündner Flecken Vals. Dort wollte der
              einheimische Unternehmer Remo Stoffel einst einen 381 Meter hohen
              Hotelturm bauen, doch daraus wird wohl nichts. Dafür investierte
              er 60 Millionen ins 7132 und besitzt nun zumindest eines der
              aufregendsten Fünfstern-Superior-Häuser der Alpen. Ein
              architektonisches Meisterwerk, das an Exklusivität kaum zu
              überbieten ist. Stünde es in einer klassischen
              Wintersportdestination, wäre das 7132 mitten unter den
              Allerbesten.
            </p>

            <ArticleList />

            <p>
              Die Umfrage unter den im Rating aufgeführten Hotels lässt keine
              Zweifel offen: Der Turnaround ist geschafft. So gut wie alle
              Häuser legten im vergangenen Sommer zu. Die meisten freuten sich
              über einen Gästezuwachs von gegen zehn Prozent. Für die
              deutlichsten Ausreisser nach oben sorgten die St. Moritzer
              Herbergen Badrutt's Palace, Kulm und Giardino Mountain sowie das
              Cervo Zermatt; Lenkerhof und Alpina Gstaad erlebten den besten
              Sommer überhaupt.
            </p>

            <p>
              Auch der Buchungseingang für den Winter stimmt optimistisch. Alles
              deutet darauf hin, dass die Erstarkung des Euro wieder mehr
              europäische Gäste anlockt. Und dass es zu einem Allzeithoch an
              Schweizer Gästen kommt. Bemerkenswert: Derweil beim Konkurrenten
              Österreich die Preise steigen, sind sie in den Schweizer Bergen im
              vergangenen Sommer um knapp zehn Prozent gesunken. Im Winter wird
              der Gast für sein Geld mehr erhalten denn je.
            </p>
          </div>
        </main>

        <Sidebar title='So wurde bewertet'>
          <p>
            <strong>Karl Wild</strong> ist Verfasser des Ratings mit den besten
            Winterhotels. Heute präsentiert die SonntagsZeitung das Rating zum
            siebten Mal. Wild arbeitet als Publizist, Hotel- und
            Restauranttester in Langnau am Albis ZH. Er und sein Team, mehr als
            ein Dutzend Spezialisten aus Hotellerie und Tourismus sowie
            Vielreisende, haben alle Hotels besucht. Um die Chancengleichheit zu
            wahren, werden nur Hotels mit mindestens 12 Zimmern und eigenem
            Restaurant berücksichtigt.
          </p>

          <h4 className='ui infographic dividing header'>Die Kriterien</h4>

          <ul className='ui infographic list'>
            <li className='item'>
              Wertung der wichtigen Hotel- und Restaurantführer
            </li>
            <li className='item'>
              Qualitätskontrollen führender Hotelvereinigungen
            </li>
            <li className='item'>Investitionstätigkeit</li>
            <li className='item'>Gastfreundschaft</li>
            <li className='item'>
              Charisma und Innovationsfreude des Hoteliers
            </li>
            <li className='item'>Charakter und Originalität des Hauses</li>
            <li className='item'>Lage und Umgebung</li>
            <li className='item'>Wintersport- und Freizeitangebot</li>
            <li className='item'>Preis-Leistungs-Verhältnis</li>
            <li className='item'>Gästebewertungen</li>
            <li className='item'>Subjektiver Gesamteindruck</li>
          </ul>
        </Sidebar>

        <CategoryMenu />

        <HotelCategory
          category='Die 25 besten Winterhotels mit 5 Sternen'
          numberOfStars={5}
          hotelList={this.state.hotelsWith5Stars}
          id='cat-5'
          request={this.state.request}
        />
        <HotelCategory
          category='Die 25 besten Winterhotels mit 4 Sternen'
          numberOfStars={4}
          id='cat-4'
          hotelList={this.state.hotelsWith4Stars}
          request={this.state.request}
        />
        <HotelCategory
          category='Die 25 besten Winterhotels mit 3 Sternen'
          numberOfStars={3}
          id='cat-3'
          hotelList={this.state.hotelsWith3Stars}
          request={this.state.request}
        />

        <footer className='ui vertical attached segment'>
          <div className='ui container'>
            <div className='ui stackable grid'>
              <div className='centered two column row'>
                <div className='column'>
                  <ArticleTeasers
                    articleIds={['15273943']}
                    numberOfCards='one'
                  />
                </div>
              </div>
              <div className='two column row'>
                <div className='column'>
                  <table className='ui very basic definition infographic small unstackable table'>
                    <tbody>
                      <tr className='top aligned'>
                        <td>Hotelranking und Texte</td>
                        <td>Karl Wild</td>
                      </tr>
                      <tr className='top aligned'>
                        <td>Digitale Umsetzung</td>
                        <td>Kaspar Manz, Interaktiv-Team</td>
                      </tr>
                      {this.state.publishedTimestamp > 0 && (
                        <tr>
                          <td>Veröffentlicht</td>
                          <td>
                            <TimestampFormatter
                              timestamp={this.state.publishedTimestamp}
                            />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>Aktualisierung</td>
                        <td>
                          <TimestampFormatter
                            timestamp={window.project.buildTimestamp}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Version</td>
                        <td>{window.project.version}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className='column'>
                  <FeedbackMessage />
                  <ShareButtons
                    articleID={this.props.articleId}
                    className='basic'
                    hashtags={['holidays', 'switzerland', 'hotels']}
                  />
                  <Tracking articleId={this.props.articleId} />
                </div>
              </div>
              <div className='one column row'>
                <div className='column'>
                  <PureCopyrightNotice
                    name='SonntagsZeitung'
                    secondLevelDomainName='sonntagszeitung'
                  />
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/main/index.js